// 클라이언트 판정 호출 (§10·§12). 우리 서버 프록시(/api/judge)로 POST한다.
// 클라이언트는 절대 Gemini 키를 들지 않는다 — 프록시가 들고 레이트리밋도 건다.
// 실패/타임아웃이면 throw → verdict가 fallbackJudgement로 폴백(루프 안 끊김).

import type { Judge, JudgeInput } from "./judge";
import type { Judgement, Lang, RawSignals } from "./types";

/** 서버로 보내는 채점 요청. AI가 필요한 것만 — eventTimestamps 같은 원시 타임라인은 안 보냄. */
export interface JudgeRequest {
  lang: Lang;
  archetype: string;
  behaviorScore: number;
  stats: {
    charCount: number;
    insertCount: number;
    deleteCount: number;
    exclaimCount: number;
    questionCount: number;
    capsRun: number;
    repeatRun: number;
    fillerCount: number;
    durationMs: number;
    startDelayMs: number;
    mode: string;
    peakVolume?: number;
    pitchVariance?: number;
    speechRate?: number;
    pauseCount?: number;
    pauseRatio?: number;
  };
  // content(쓴 글)는 채점 순간에만 전송, 서버는 저장 안 함(§12).
  content: string;
}

function buildRequest(input: JudgeInput): JudgeRequest {
  const s: RawSignals = input.signals;
  return {
    lang: input.lang,
    archetype: input.archetype,
    behaviorScore: Math.round(input.score),
    stats: {
      charCount: s.charCount,
      insertCount: s.insertCount,
      deleteCount: s.deleteCount,
      exclaimCount: s.exclaimCount,
      questionCount: s.questionCount,
      capsRun: s.capsRun,
      repeatRun: s.repeatRun,
      fillerCount: s.fillerCount,
      durationMs: Math.round(s.durationMs),
      startDelayMs: Math.round(s.startDelayMs),
      mode: s.mode,
      peakVolume: s.voice?.peakVolume,
      pitchVariance: s.voice?.pitchVariance,
      speechRate: s.voice?.speechRate,
      pauseCount: s.voice?.pauseCount,
      pauseRatio: s.voice?.pauseRatio,
    },
    content: s.text,
  };
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function clampFactor(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.max(0, Math.min(1.2, n));
}

/** 서버 응답을 Judgement로 안전하게 강제 변환. 핵심 필드 빠지면 throw. */
export function validateJudgement(data: unknown): Judgement {
  if (typeof data !== "object" || data === null) {
    throw new Error("judge: invalid response shape");
  }
  const d = data as Record<string, unknown>;
  const crisis = d.crisis === true;
  const off_scope = d.off_scope === true;
  if (typeof d.headline !== "string" && !crisis && !off_scope) {
    throw new Error("judge: missing headline");
  }
  return {
    content_factor: clampFactor(d.content_factor),
    crisis,
    off_scope,
    headline: typeof d.headline === "string" ? d.headline : "",
    archetype_line: typeof d.archetype_line === "string" ? d.archetype_line : "",
    report_notes: asStringArray(d.report_notes),
    step_back: typeof d.step_back === "string" ? d.step_back : "",
  };
}

export interface HttpJudgeOptions {
  timeoutMs?: number; // 기본 12000. Cloud Run 콜드 스타트 커버(가짜 과학 로딩이 덮음).
  retries?: number; // 기본 1. 1차가 콜드 스타트로 실패해도 인스턴스가 데워졌으니 재시도는 빠르게 성공.
  fetchImpl?: typeof fetch; // 테스트 주입용.
}

/** 엔드포인트로 POST하는 Judge. 콜드 스타트 대비 재시도 포함. 모두 실패 시 throw. */
export function httpJudge(endpoint: string, opts: HttpJudgeOptions = {}): Judge {
  const doFetch = opts.fetchImpl ?? fetch;
  const timeoutMs = opts.timeoutMs ?? 12000;
  const retries = opts.retries ?? 1;
  return {
    async judge(input: JudgeInput): Promise<Judgement> {
      const body = JSON.stringify(buildRequest(input));
      let lastErr: unknown;
      for (let attempt = 0; attempt <= retries; attempt++) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), timeoutMs);
        try {
          const res = await doFetch(endpoint, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body,
            signal: ctrl.signal,
          });
          if (!res.ok) throw new Error(`judge: HTTP ${res.status}`);
          return validateJudgement(await res.json());
        } catch (e) {
          lastErr = e; // 1차 실패(콜드 스타트 타임아웃 등) → 데워진 인스턴스로 재시도
        } finally {
          clearTimeout(timer);
        }
      }
      throw lastErr;
    },
  };
}
